import { useEffect, useState } from "react";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import {
  type DashCard, type DashCalc, type DashChartType, type DashGroupBy, type DashKind, type DashMetric, type DashMetricStyle,
  type DashItemType, type DashAlign, type DashItemLayout,
  CALC_OPTIONS, CALC_LABELS, CHART_LABELS, GROUPBY_LABELS, FONT_WEIGHTS, ITEM_TYPE_LABELS, ALIGN_OPTIONS, LAYOUT_OPTIONS,
  DEFAULT_METRIC_FONT_SIZE, DEFAULT_METRIC_FONT_WEIGHT, LABEL_DEFAULTS, TITLE_DEFAULTS, newCardId, newMetricId, cardMetrics, isValueItem,
} from "../../lib/dashboard";
import { BUILDER_ROW_H, BUILDER_ROW_GAP } from "../../lib/formLayout";
import { type Formula, formulaRefs } from "../../lib/formFormula";
import { FormulaEditor } from "../forms/FormulaEditor";

export interface CatalogForm {
  formId: string;
  formName: string;
  status: string;
  tesisName: string | null;
  numericFields: Array<{ id: string; label: string; unit: string | null }>;
}

interface Props {
  open: boolean;
  card: DashCard | null;
  catalog: CatalogForm[];
  onClose: () => void;
  onSave: (card: DashCard) => void;
}

/** Editör-içi öğe durumu (KPI kartındaki bir öğe: metric/static/note/heading/divider + stil). */
interface EditorItem {
  key: string;
  type: DashItemType;
  formId: string;      // metric
  fieldId: string;     // metric
  calc: DashCalc;      // metric
  value: string;       // static (sayı, string state)
  formula: Formula;    // formula (kart-içi dört işlem)
  text: string;        // note/heading
  label: string;       // metric/static
  unit: string;        // static
  layout: DashItemLayout; // metric/static: etiket alt alta mı yan yana mı
  fontSize: number;       // ── DEĞER stili ──
  fontWeight: number;
  customColor: boolean;
  color: string;
  align: DashAlign;
  alignTouched: boolean;  // kullanıcı hizayı BİLİNÇLİ seçti mi (yerleşim değişiminde korunur)
  lblCustom: boolean;    // ── ETİKET stili (opt-in; kapalıyken hiç yazılmaz → sınıf varsayılanı) ──
  lblFontSize: number;
  lblFontWeight: number;
  lblCustomColor: boolean;
  lblColor: string;
  lblCaps: boolean;
}

/** Sunucu şemasıyla ayna sınırlar — aşılırsa TÜM pano kaydı reddedilir (form.ts DashCardSchema/DashMetricSchema). */
const MAX_CARD_ROWS = 20;
const MAX_TITLE = 120, MAX_TEXT = 500, MAX_LABEL = 160, MAX_UNIT = 24;
/** Izgara kolon genişliği tahmini (px) — sarma satır sayısı hesabı için kaba ölçü. */
const APPROX_COL_PX = 96;

/** Yeni formülün başlangıcı — kullanıcı operandı seçene kadar sabit 0 (geçerli ama etkisiz). */
const EMPTY_FORMULA: Formula = { start: { kind: "const", value: 0 }, steps: [] };

const defaultFontFor = (type: DashItemType) => (type === "heading" ? 15 : type === "note" ? 12 : DEFAULT_METRIC_FONT_SIZE);
const clampFont = (n: number, fallback: number) => Math.round(Math.min(96, Math.max(8, n || fallback)));
const alignDefaultFor = (layout: DashItemLayout): DashAlign => (layout === "row" ? "right" : "left");

function toEditorItem(m: DashMetric): EditorItem {
  const st = m.style ?? {};
  const ls = m.labelStyle ?? null;
  const type = m.type ?? "metric";
  const layout: DashItemLayout = m.layout ?? "stack";
  const ld = LABEL_DEFAULTS[layout];
  return {
    key: m.id || newMetricId(), type,
    formId: m.formTemplateId ?? "", fieldId: m.fieldId ?? "", calc: m.calc ?? "sum",
    value: m.value != null ? String(m.value) : "0",
    formula: m.formula ?? EMPTY_FORMULA,
    text: m.text ?? "",
    label: m.label ?? "", unit: m.unit ?? "",
    layout,
    fontSize: st.fontSize ?? defaultFontFor(type),
    fontWeight: st.fontWeight ?? DEFAULT_METRIC_FONT_WEIGHT,
    customColor: !!st.color, color: st.color || "#14253e",
    align: st.align ?? alignDefaultFor(layout),
    // Yerleşim varsayılanından FARKLI kayıtlı hiza = bilinçli seçim → yerleşim değişiminde korunur.
    alignTouched: st.align != null && st.align !== alignDefaultFor(layout),
    lblCustom: !!ls,
    lblFontSize: ls?.fontSize ?? ld.fontSize,
    lblFontWeight: ls?.fontWeight ?? ld.fontWeight,
    lblCustomColor: !!ls?.color, lblColor: ls?.color || "#52525b",
    lblCaps: ls?.caps ?? ld.caps,
  };
}
function blankItem(type: DashItemType, catalog: CatalogForm[]): EditorItem {
  const f0 = catalog.find((c) => c.numericFields.length > 0) ?? catalog[0];
  const ld = LABEL_DEFAULTS.stack;
  return {
    key: newMetricId(), type,
    formId: type === "metric" ? (f0?.formId ?? "") : "",
    fieldId: type === "metric" ? (f0?.numericFields[0]?.id ?? "") : "",
    calc: "sum", value: "0",
    formula: EMPTY_FORMULA,
    text: type === "note" ? "Bilgi notu" : type === "heading" ? "Başlık" : "",
    label: "", unit: "",
    layout: "stack",
    fontSize: defaultFontFor(type),
    fontWeight: type === "note" ? 500 : DEFAULT_METRIC_FONT_WEIGHT,
    customColor: false, color: "#14253e", align: "left", alignTouched: false,
    lblCustom: false, lblFontSize: ld.fontSize, lblFontWeight: ld.fontWeight,
    lblCustomColor: false, lblColor: "#52525b", lblCaps: ld.caps,
  };
}
function buildItem(em: EditorItem): DashMetric {
  const style: DashMetricStyle = {
    fontSize: clampFont(em.fontSize, defaultFontFor(em.type)),
    fontWeight: em.fontWeight,
  };
  // Hiza YALNIZ yerleşim varsayılanından farklıysa yazılır → tekrar açıldığında "bilinçli seçim" olarak tanınır
  // (yoksa her öğe dokunulmuş sayılır ve yerleşime göre otomatik taşıma hiç çalışmaz).
  if (em.align !== alignDefaultFor(em.layout)) style.align = em.align;
  if (em.customColor && /^#[0-9a-fA-F]{6}$/.test(em.color)) style.color = em.color;
  const out: DashMetric = { id: em.key, type: em.type, style };
  if (em.type === "metric" || em.type === "static" || em.type === "formula") {
    out.layout = em.layout;
    if (em.lblCustom) {
      const ls: DashMetricStyle = {
        fontSize: clampFont(em.lblFontSize, LABEL_DEFAULTS[em.layout].fontSize),
        fontWeight: em.lblFontWeight,
        caps: em.lblCaps,
      };
      if (em.lblCustomColor && /^#[0-9a-fA-F]{6}$/.test(em.lblColor)) ls.color = em.lblColor;
      out.labelStyle = ls;
    }
  }
  if (em.type === "metric") { out.formTemplateId = em.formId; out.fieldId = em.fieldId; out.calc = em.calc; out.label = em.label.trim() || undefined; }
  else if (em.type === "static") { out.value = Number(em.value) || 0; out.label = em.label.trim() || undefined; out.unit = em.unit.trim() || undefined; }
  else if (em.type === "formula") { out.formula = em.formula; out.label = em.label.trim() || undefined; out.unit = em.unit.trim() || undefined; }
  else if (em.type === "note" || em.type === "heading") { out.text = em.text; }
  return out;
}

/** Öğe listesinin kaç ızgara satırı tuttuğunun tahmini — hem yeni içerik hem KAYITLI içerik için kullanılır
 *  (ikisinin farkı = kullanıcının elle sürükleyip eklediği fazlalık, düzenlemede korunur). */
function estimateRows(built: DashMetric[], cardW: number, titleFs: number = TITLE_DEFAULTS.fontSize): number {
  const valueItems = built.filter(isValueItem).length;
  const showLabels = valueItems > 1 || built.length > 1;
  // Not/başlık SARAR (pre-wrap + word-break): kolon genişliğinden satır sayısını tahmin et,
  // yoksa çok satırlı not .dash-kpi-metrics'in overflow:hidden'ı altında kırpılır.
  const contentPx = Math.max(60, cardW * APPROX_COL_PX - 31); // 31 = kart yatay padding (14+17)
  const wrappedLines = (text: string | undefined, fs: number) => {
    const perLine = Math.max(8, Math.floor(contentPx / (fs * 0.55)));
    return (text ?? "").split("\n").reduce((n, seg) => n + Math.max(1, Math.ceil(seg.length / perLine)), 0);
  };
  const bodyPx = built.reduce((a, m) => {
    const t = m.type ?? "metric";
    if (t === "divider") return a + 13;
    if (t === "note") { const fs = m.style?.fontSize ?? 12; return a + wrappedLines(m.text, fs) * fs * 1.45 + 4; }
    if (t === "heading") { const fs = m.style?.fontSize ?? 15; return a + wrappedLines(m.text, fs) * fs * 1.35 + 4; }
    const vf = m.style?.fontSize ?? DEFAULT_METRIC_FONT_SIZE;
    const layout = m.layout ?? "stack";
    const lf = m.labelStyle?.fontSize ?? LABEL_DEFAULTS[layout].fontSize;
    // Yan yana: etiket ve değer TEK satır → en büyüğü kadar yer kaplar.
    if (layout === "row") return a + Math.max(vf, lf) * 1.35;
    return a + vf * 1.35 + (showLabels ? lf * 1.45 : 0);
  }, 0) + Math.max(0, built.length - 1) * 10;
  return Math.ceil((bodyPx + headerPx(titleFs)) / (BUILDER_ROW_H + BUILDER_ROW_GAP));
}
/** Başlık şeridinin kapladığı yükseklik — büyütülmüş başlık gövdeyi ezmesin diye pay da büyür (KPI + grafik ORTAK). */
function headerPx(titleFs: number): number {
  return Math.max(44, titleFs * 1.6 + 26);
}
/** Grafik gövdesinin okunur kalması için gereken en az yükseklik (SVG + alt açıklama satırı). */
const MIN_CHART_BODY_PX = 150;

const ADD_TYPES: Array<{ type: DashItemType; icon: string }> = [
  { type: "metric", icon: "hash" }, { type: "static", icon: "hash" }, { type: "formula", icon: "sigma" },
  { type: "note", icon: "fileText" }, { type: "heading", icon: "heading" }, { type: "divider", icon: "minus" },
];

/** Kart editörü: KPI kartına metrik + sabit sayı + bilgi notu + başlık + çizgi öğeleri; her değer hizalanabilir/stillenebilir. Grafik kartı tek kaynak. */
export function DashCardEditorDrawer({ open, card, catalog, onClose, onSave }: Props) {
  const isEdit = !!card;
  const [kind, setKind] = useState<DashKind>("kpi");
  const [title, setTitle] = useState("");
  // Kart başlığı (.head .lbl) biçimi — opt-in; kapalıyken hiç yazılmaz, kanonik sınıf varsayılanı geçerli.
  const [titleCustom, setTitleCustom] = useState(false);
  const [titleFontSize, setTitleFontSize] = useState(TITLE_DEFAULTS.fontSize);
  const [titleFontWeight, setTitleFontWeight] = useState(TITLE_DEFAULTS.fontWeight);
  const [titleCustomColor, setTitleCustomColor] = useState(false);
  const [titleColor, setTitleColor] = useState("#71717a");
  const [titleCaps, setTitleCaps] = useState(TITLE_DEFAULTS.caps);
  const [titleAlign, setTitleAlign] = useState<DashAlign>("left");
  const [items, setItems] = useState<EditorItem[]>([]);
  const [chFormId, setChFormId] = useState("");
  const [chFieldId, setChFieldId] = useState("");
  const [chCalc, setChCalc] = useState<DashCalc>("sum");
  const [chartType, setChartType] = useState<DashChartType>("bar");
  const [groupBy, setGroupBy] = useState<DashGroupBy>("day");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    const ts = card?.titleStyle ?? null;
    setTitleCustom(!!ts);
    setTitleFontSize(ts?.fontSize ?? TITLE_DEFAULTS.fontSize);
    setTitleFontWeight(ts?.fontWeight ?? TITLE_DEFAULTS.fontWeight);
    setTitleCustomColor(!!ts?.color); setTitleColor(ts?.color || "#71717a");
    setTitleCaps(ts?.caps ?? TITLE_DEFAULTS.caps);
    setTitleAlign(ts?.align ?? "left");
    if (card) {
      setKind(card.kind); setTitle(card.title);
      setItems(cardMetrics(card).map(toEditorItem));
      setChFormId(card.formTemplateId); setChFieldId(card.fieldId); setChCalc(card.calc);
      setChartType(card.chartType ?? "bar"); setGroupBy(card.groupBy ?? "day");
    } else {
      const bm = blankItem("metric", catalog);
      setKind("kpi"); setTitle(""); setItems([bm]);
      setChFormId(bm.formId); setChFieldId(bm.fieldId); setChCalc("sum"); setChartType("bar"); setGroupBy("day");
    }
  }, [open, card, catalog]);

  const fieldsFor = (formId: string) => catalog.find((c) => c.formId === formId)?.numericFields ?? [];
  function update(key: string, patch: Partial<EditorItem>) { setItems((xs) => xs.map((m) => (m.key === key ? { ...m, ...patch } : m))); }
  function onItemFormChange(key: string, formId: string) { update(key, { formId, fieldId: fieldsFor(formId)[0]?.id ?? "" }); }
  function addItem(type: DashItemType) {
    setItems((xs) => {
      const it = blankItem(type, catalog);
      // Formül eklerken başlangıcı ilk değer-öğesine bağla (boş sabitle açmak yerine kullanışlı varsayılan).
      if (type === "formula") {
        const first = xs.find((x) => x.type === "metric" || x.type === "static" || x.type === "formula");
        if (first) it.formula = { start: { kind: "field", ref: first.key }, steps: [] };
      }
      return [...xs, it];
    });
  }
  /** Formül operandı olabilecek ÖTEKİ öğeler (kendisi hariç → doğrudan öz-referans engellenir).
   *  Sıra numarası TAM items dizisinden gelir (editördeki "Metrik 3" başlığıyla birebir aynı olsun diye);
   *  otomatik etikete form + tesis adı da girer — özelliğin ana senaryosu farklı tesislerin AYNI isimli
   *  alanlarını kıyaslamak olduğundan yalnız "alan · hesap" ayırt edici değil. */
  function formulaCandidates(self: EditorItem): Array<{ refKey: string; label: string }> {
    return items
      .map((x, i) => ({ x, no: i + 1 }))
      .filter(({ x }) => x.key !== self.key && (x.type === "metric" || x.type === "static" || x.type === "formula"))
      .map(({ x, no }) => {
        let auto: string;
        if (x.type === "metric") {
          const c = catalog.find((y) => y.formId === x.formId);
          const f = fieldsFor(x.formId).find((y) => y.id === x.fieldId);
          const src = `${c?.formName ?? "Form"}${c?.tesisName ? ` · ${c.tesisName}` : ""}`;
          auto = `${src} · ${f?.label ?? "Alan"} · ${CALC_LABELS[x.calc]}`;
        } else auto = x.type === "static" ? `Sabit ${x.value}` : "Hesaplanan";
        return { refKey: x.key, label: `${no}. ${x.label.trim() || auto}` };
      });
  }
  /** Formül döngüsü var mı (A→B→A) — kaydetmeden önce uyar; değerlendirici null üretirdi. */
  function hasFormulaCycle(list: EditorItem[]): boolean {
    const byKey = new Map(list.map((x) => [x.key, x]));
    const state = new Map<string, 0 | 1 | 2>();
    const dfs = (key: string): boolean => {
      const st = state.get(key);
      if (st === 1) return true;
      if (st === 2) return false;
      const it = byKey.get(key);
      if (!it || it.type !== "formula") { state.set(key, 2); return false; }
      state.set(key, 1);
      for (const ref of formulaRefs(it.formula)) if (dfs(ref)) return true;
      state.set(key, 2);
      return false;
    };
    return list.some((x) => x.type === "formula" && dfs(x.key));
  }
  function removeItem(key: string) { setItems((xs) => (xs.length > 1 ? xs.filter((m) => m.key !== key) : xs)); }
  /** Öğeyi listede bir sıra yukarı/aşağı taşı (kart içindeki görsel sıra = bu dizi sırası). */
  function moveItem(key: string, dir: -1 | 1) {
    setItems((xs) => {
      const i = xs.findIndex((m) => m.key === key);
      const j = i + dir;
      const a = xs[i], b = xs[j];
      if (i < 0 || j < 0 || j >= xs.length || !a || !b) return xs;
      const next = [...xs];
      next[i] = b; next[j] = a;
      return next;
    });
  }
  /** Yerleşim değişimi: hiza yalnız DOKUNULMAMIŞSA yeni yerleşimin varsayılanına taşınır (bilinçli seçim her turda korunur). */
  function setItemLayout(key: string, layout: DashItemLayout) {
    setItems((xs) => xs.map((m) => {
      if (m.key !== key || m.layout === layout) return m;
      const ld = LABEL_DEFAULTS[layout];
      const align = m.alignTouched ? m.align : alignDefaultFor(layout);
      // Etiket özelleştirilmediyse (yazılmıyor) kontrol değerlerini yeni yerleşimin varsayılanına tazele.
      const lbl = m.lblCustom ? {} : { lblFontSize: ld.fontSize, lblFontWeight: ld.fontWeight, lblCaps: ld.caps };
      return { ...m, layout, align, ...lbl };
    }));
  }
  /** Etiket özelleştirme açılırken kontrolleri o anki yerleşimin varsayılanından tohumla. */
  function toggleLabelCustom(key: string, on: boolean) {
    setItems((xs) => xs.map((m) => {
      if (m.key !== key) return m;
      if (!on) return { ...m, lblCustom: false };
      const ld = LABEL_DEFAULTS[m.layout];
      return { ...m, lblCustom: true, lblFontSize: m.lblFontSize || ld.fontSize, lblFontWeight: m.lblFontWeight || ld.fontWeight };
    }));
  }
  function onChFormChange(id: string) { setChFormId(id); setChFieldId(fieldsFor(id)[0]?.id ?? ""); }

  function defaultTitle(): string {
    if (kind === "chart") { const f = fieldsFor(chFormId).find((x) => x.id === chFieldId); return `${f?.label ?? "Alan"} · ${CHART_LABELS[chartType]}`; }
    const m0 = items.find((m) => m.type === "metric" || m.type === "static");
    const f = m0 ? fieldsFor(m0.formId).find((x) => x.id === m0.fieldId) : null;
    // || (?? değil): etiket BOŞ STRING olabilir; ?? boş string'i "dolu" sayıp "Kart" yedeğine hiç düşmez.
    return `${f?.label || m0?.label || "Kart"}${items.length > 1 ? ` +${items.length - 1}` : ""}`;
  }

  /** Başlık stili — yalnız özelleştirme açıksa yazılır (kapalı → alan hiç gitmez, kanonik görünüm korunur). */
  function buildTitleStyle(): DashMetricStyle | undefined {
    if (!titleCustom) return undefined;
    const st: DashMetricStyle = {
      fontSize: clampFont(titleFontSize, TITLE_DEFAULTS.fontSize),
      fontWeight: titleFontWeight,
      caps: titleCaps,
    };
    if (titleAlign !== "left") st.align = titleAlign;
    if (titleCustomColor && /^#[0-9a-fA-F]{6}$/.test(titleColor)) st.color = titleColor;
    return st;
  }

  function save() {
    const sameKind = card && card.kind === kind;
    const finalTitle = title.trim() || defaultTitle();
    const titleStyle = buildTitleStyle();
    if (kind === "chart") {
      if (!chFormId || !chFieldId) { setError("Grafik için form ve sayısal alan seçin."); return; }
      // Başlık büyütülürse gövde (.dash-chart-body flex:1) ezilip grafik kaybolur → kart en az başlık+gövde kadar yüksek olsun.
      const minChartRows = Math.ceil((headerPx(titleStyle?.fontSize ?? TITLE_DEFAULTS.fontSize) + MIN_CHART_BODY_PX) / (BUILDER_ROW_H + BUILDER_ROW_GAP));
      onSave({
        id: card?.id ?? newCardId(), kind: "chart", title: finalTitle, titleStyle,
        formTemplateId: chFormId, fieldId: chFieldId, calc: chCalc, chartType, groupBy,
        x: card?.x ?? 0, y: card?.y ?? 0, w: sameKind ? card!.w : 6,
        h: Math.min(MAX_CARD_ROWS, Math.max(sameKind ? card!.h : 4, minChartRows)),
      });
      return;
    }
    if (items.length === 0) { setError("En az bir öğe ekleyin."); return; }
    for (const m of items) if (m.type === "metric" && (!m.formId || !m.fieldId)) { setError("Her metrik öğesi için form ve sayısal alan seçin."); return; }
    // Silinen öğeye referans kalmış olabilir (operand seçildikten sonra o öğe kaldırılırsa) → değer null olurdu.
    const liveKeys = new Set(items.map((x) => x.key));
    for (const m of items) {
      if (m.type !== "formula") continue;
      const dead = formulaRefs(m.formula).find((ref) => !liveKeys.has(ref));
      if (dead) { setError("Bir formül, karttan kaldırılmış bir öğeye referans veriyor. Operandları güncelleyin."); return; }
    }
    if (hasFormulaCycle(items)) { setError("Formüller birbirine döngüsel referans veriyor (ör. A → B → A)."); return; }
    const built: DashMetric[] = items.map(buildItem);
    // Üst-seviye alanlar (şema için): ilk metrik öğesinden; yoksa katalogdan yer tutucu (KPI'da kullanılmaz).
    const firstMetric = built.find((b) => (b.type ?? "metric") === "metric" && b.formTemplateId);
    const anyForm = catalog.find((c) => c.numericFields.length > 0) ?? catalog[0];
    const topForm = firstMetric?.formTemplateId ?? anyForm?.formId ?? "";
    const topField = firstMetric?.fieldId ?? anyForm?.numericFields[0]?.id ?? "";
    const topCalc = firstMetric?.calc ?? "sum";
    // Yükseklik: içeriğe göre tahmin — hem BÜYÜR hem KÜÇÜLÜR (yan yana yerleşim kartı gerçekten sıkıştırsın).
    const cardW = sameKind ? card!.w : 3;
    // "Kayıtlı h > tahmin ise fazlalık kasıtlıdır" sezgiseli KULLANILMIYOR: elle sürükleme ile eski
    // (yalnız-büyüyen) mantığın bıraktığı şişkinlik ayırt edilemez → tüm mevcut kartlarda küçülme ölürdü.
    // Kural: editörde kaydetmek kartı içeriğine oturtur; ince ayar ızgarada sürükleyerek yapılır.
    const neededRows = estimateRows(built, cardW, titleStyle?.fontSize ?? TITLE_DEFAULTS.fontSize);
    onSave({
      id: card?.id ?? newCardId(), kind: "kpi", title: finalTitle, titleStyle,
      formTemplateId: topForm, fieldId: topField, calc: topCalc, metrics: built,
      x: card?.x ?? 0, y: card?.y ?? 0, w: sameKind ? card!.w : 3,
      // MAX_CARD_ROWS sunucu şemasıyla ayna — aşılırsa DashCardSchema TÜM panonun kaydını reddeder.
      h: Math.min(MAX_CARD_ROWS, Math.max(neededRows, 2)),
    });
  }

  // ── Ortak stil kontrolleri (renk + hizalama) ──
  const colorControl = (m: EditorItem) => (
    <div className="form-field">
      <label className="lbl">Renk</label>
      <div className="dash-color-row">
        <label className="dash-color-toggle" title="Özel renk (kapalı → tema)">
          <input type="checkbox" checked={m.customColor} onChange={(e) => update(m.key, { customColor: e.target.checked })} />Özel
        </label>
        <input type="color" value={m.color} disabled={!m.customColor} onChange={(e) => update(m.key, { color: e.target.value })} aria-label="Renk seç" />
      </div>
    </div>
  );
  const fontSizeControl = (m: EditorItem) => (
    <div className="form-field">
      <label className="lbl">Font boyutu</label>
      <input className="form-input" type="number" min={8} max={96} step={1} value={m.fontSize}
        onChange={(e) => update(m.key, { fontSize: Math.round(Number(e.target.value)) || defaultFontFor(m.type) })}
        onBlur={() => update(m.key, { fontSize: clampFont(m.fontSize, defaultFontFor(m.type)) })} />
    </div>
  );
  const weightControl = (m: EditorItem) => (
    <div className="form-field">
      <label className="lbl">Kalınlık</label>
      <select className="form-select" value={m.fontWeight} onChange={(e) => update(m.key, { fontWeight: Number(e.target.value) })}>
        {FONT_WEIGHTS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
      </select>
    </div>
  );
  const alignControl = (m: EditorItem) => (
    <div className="form-field">
      <label className="lbl">Değer hizası</label>
      <div className="dash-seg dash-align-seg" role="group" aria-label="Değer hizası">
        {ALIGN_OPTIONS.map((a) => (
          <button key={a.value} type="button" className={m.align === a.value ? "on" : ""} title={a.label} aria-label={a.label} onClick={() => update(m.key, { align: a.value, alignTouched: true })}>
            <Icon name={a.icon} size={14} />
          </button>
        ))}
      </div>
    </div>
  );
  const layoutControl = (m: EditorItem) => (
    <div className="form-field">
      <label className="lbl">Yerleşim</label>
      <div className="dash-seg" role="group" aria-label="Etiket-değer yerleşimi">
        {LAYOUT_OPTIONS.map((o) => (
          <button key={o.value} type="button" className={m.layout === o.value ? "on" : ""} title={o.hint} onClick={() => setItemLayout(m.key, o.value)}>{o.label}</button>
        ))}
      </div>
    </div>
  );
  /** Etiketin DEĞERDEN BAĞIMSIZ biçimi — opt-in; kapalıyken stil yazılmaz, sınıf varsayılanı geçerli olur.
   *  Kapalıyken tek onay kutusu (1 satır), açıkken kontroller AYNI satırda yanına dizilir. */
  const labelStyleBlock = (m: EditorItem) => (
    <div className={`dash-me-lblrow${m.lblCustom ? " on" : ""}`}>
      <label className="dash-color-toggle" title="Etiketi değerden bağımsız biçimlendir">
        <input type="checkbox" checked={m.lblCustom} onChange={(e) => toggleLabelCustom(m.key, e.target.checked)} />
        Etiket biçimi
      </label>
      {m.lblCustom ? (
        <>
          <div className="form-field">
            <label className="lbl">Boyut</label>
            <input className="form-input" type="number" min={8} max={96} step={1} value={m.lblFontSize}
              onChange={(e) => update(m.key, { lblFontSize: Math.round(Number(e.target.value)) || LABEL_DEFAULTS[m.layout].fontSize })}
              onBlur={() => update(m.key, { lblFontSize: clampFont(m.lblFontSize, LABEL_DEFAULTS[m.layout].fontSize) })} />
          </div>
          <div className="form-field">
            <label className="lbl">Kalınlık</label>
            <select className="form-select" value={m.lblFontWeight} onChange={(e) => update(m.key, { lblFontWeight: Number(e.target.value) })}>
              {FONT_WEIGHTS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="lbl">Renk</label>
            <div className="dash-color-row">
              <label className="dash-color-toggle" title="Özel renk (kapalı → tema)">
                <input type="checkbox" checked={m.lblCustomColor} onChange={(e) => update(m.key, { lblCustomColor: e.target.checked })} />Özel
              </label>
              <input type="color" value={m.lblColor} disabled={!m.lblCustomColor} onChange={(e) => update(m.key, { lblColor: e.target.value })} aria-label="Etiket rengi seç" />
            </div>
          </div>
          <label className="dash-color-toggle" title="Etiketi büyük harfe çevir">
            <input type="checkbox" checked={m.lblCaps} onChange={(e) => update(m.key, { lblCaps: e.target.checked })} />BÜYÜK
          </label>
        </>
      ) : null}
    </div>
  );

  return (
    <Drawer
      open={open} onClose={onClose}
      title={isEdit ? "Kartı Düzenle" : "Yeni Kart"}
      subtitle="KPI kartına değer, sabit sayı, not, başlık ve çizgi ekleyip her birini biçimlendirin"
      size="3xl" dismissible={false}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
          <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
          <button type="button" className="btn-md primary" onClick={save} disabled={catalog.length === 0}>
            <Icon name="check" size={14} />{isEdit ? "Güncelle" : "Ekle"}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? <div className="form-banner-err" role="alert"><Icon name="alertTriangle" size={14} />{error}</div> : null}
        {catalog.length === 0 ? (
          <div className="empty-state"><p className="ttl">Uygun form yok</p><p className="sub">Sayısal alan içeren en az bir form gerekir.</p></div>
        ) : (
          <>
            <div className="dash-me-r data3">
              <div className="form-field">
                <label className="lbl">Kart Türü</label>
                <div className="dash-seg" role="group" aria-label="Kart türü">
                  <button type="button" className={kind === "kpi" ? "on" : ""} onClick={() => setKind("kpi")}><Icon name="hash" size={13} />KPI</button>
                  <button type="button" className={kind === "chart" ? "on" : ""} onClick={() => setKind("chart")}><Icon name="chart" size={13} />Grafik</button>
                </div>
              </div>
              <div className="form-field dash-me-span2">
                <label className="lbl">Kart Başlığı</label>
                <input className="form-input" value={title} maxLength={MAX_TITLE} onChange={(e) => setTitle(e.target.value)} placeholder="(boş → otomatik)" />
              </div>
            </div>

            <div className={`dash-me-lblrow${titleCustom ? " on wide" : ""}`}>
              <label className="dash-color-toggle" title="Kart başlığını biçimlendir">
                <input type="checkbox" checked={titleCustom} onChange={(e) => setTitleCustom(e.target.checked)} />
                Başlık biçimi
              </label>
              {titleCustom ? (
                <>
                  <div className="form-field">
                    <label className="lbl">Boyut</label>
                    {/* Kırpma odaktan çıkışta: yazarken rakama müdahale etmez ama gösterilen değer daima kaydedilecek değere eşitlenir. */}
                    <input className="form-input" type="number" min={8} max={96} step={1} value={titleFontSize}
                      onChange={(e) => setTitleFontSize(Math.round(Number(e.target.value)) || TITLE_DEFAULTS.fontSize)}
                      onBlur={() => setTitleFontSize(clampFont(titleFontSize, TITLE_DEFAULTS.fontSize))} />
                  </div>
                  <div className="form-field">
                    <label className="lbl">Kalınlık</label>
                    <select className="form-select" value={titleFontWeight} onChange={(e) => setTitleFontWeight(Number(e.target.value))}>
                      {FONT_WEIGHTS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="lbl">Renk</label>
                    <div className="dash-color-row">
                      <label className="dash-color-toggle" title="Özel renk (kapalı → tema)">
                        <input type="checkbox" checked={titleCustomColor} onChange={(e) => setTitleCustomColor(e.target.checked)} />Özel
                      </label>
                      <input type="color" value={titleColor} disabled={!titleCustomColor} onChange={(e) => setTitleColor(e.target.value)} aria-label="Başlık rengi seç" />
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="lbl">Hiza</label>
                    <div className="dash-seg dash-align-seg" role="group" aria-label="Başlık hizası">
                      {ALIGN_OPTIONS.map((a) => (
                        <button key={a.value} type="button" className={titleAlign === a.value ? "on" : ""} title={a.label} aria-label={a.label} onClick={() => setTitleAlign(a.value)}>
                          <Icon name={a.icon} size={14} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="dash-color-toggle" title="Başlığı büyük harfe çevir">
                    <input type="checkbox" checked={titleCaps} onChange={(e) => setTitleCaps(e.target.checked)} />BÜYÜK
                  </label>
                </>
              ) : null}
            </div>

            {kind === "kpi" ? (
              <>
                <label className="lbl" style={{ marginBottom: -6 }}>Öğeler</label>
                {items.map((m, i) => {
                  const mf = fieldsFor(m.formId);
                  return (
                    <div className="dash-metric-edit" key={m.key}>
                      <div className="dash-metric-edit-head">
                        <span className="dash-metric-edit-t">{ITEM_TYPE_LABELS[m.type]} {i + 1}</span>
                        {items.length > 1 ? (
                          <span className="dash-item-acts">
                            <button type="button" className="dash-iact" aria-label="Yukarı taşı" title="Yukarı taşı" disabled={i === 0} onClick={() => moveItem(m.key, -1)}><Icon name="chevronUp" size={13} /></button>
                            <button type="button" className="dash-iact" aria-label="Aşağı taşı" title="Aşağı taşı" disabled={i === items.length - 1} onClick={() => moveItem(m.key, 1)}><Icon name="chevronDown" size={13} /></button>
                            <button type="button" className="dash-iact danger" aria-label="Öğeyi kaldır" title="Kaldır" onClick={() => removeItem(m.key)}><Icon name="trash" size={13} /></button>
                          </span>
                        ) : null}
                      </div>

                      {m.type === "metric" ? (
                        <>
                          <div className="dash-me-r data4">
                            <div className="form-field"><label className="lbl">Form</label>
                              <select className="form-select" value={m.formId} onChange={(e) => onItemFormChange(m.key, e.target.value)}>
                                {catalog.map((c) => <option key={c.formId} value={c.formId}>{c.formName}{c.tesisName ? ` · ${c.tesisName}` : ""}</option>)}
                              </select>
                            </div>
                            <div className="form-field"><label className="lbl">Alan</label>
                              <select className="form-select" value={m.fieldId} onChange={(e) => update(m.key, { fieldId: e.target.value })} disabled={mf.length === 0}>
                                {mf.length === 0 ? <option value="">— sayısal alan yok —</option> : mf.map((f) => <option key={f.id} value={f.id}>{f.label}{f.unit ? ` (${f.unit})` : ""}</option>)}
                              </select>
                            </div>
                            <div className="form-field"><label className="lbl">Hesap</label>
                              <select className="form-select" value={m.calc} onChange={(e) => update(m.key, { calc: e.target.value as DashCalc })}>
                                {CALC_OPTIONS.map((c) => <option key={c} value={c}>{CALC_LABELS[c]}</option>)}
                              </select>
                            </div>
                            <div className="form-field"><label className="lbl">Etiket</label>
                              <input className="form-input" value={m.label} maxLength={MAX_LABEL} onChange={(e) => update(m.key, { label: e.target.value })} placeholder="(boş → alan · hesap)" />
                            </div>
                          </div>
                          <div className="dash-me-r fmt">{layoutControl(m)}{alignControl(m)}{fontSizeControl(m)}{weightControl(m)}{colorControl(m)}</div>
                          {labelStyleBlock(m)}
                        </>
                      ) : m.type === "static" ? (
                        <>
                          <div className="dash-me-r data3">
                            <div className="form-field"><label className="lbl">Değer</label>
                              <input className="form-input" type="number" value={m.value} onChange={(e) => update(m.key, { value: e.target.value })} />
                            </div>
                            <div className="form-field"><label className="lbl">Birim</label>
                              <input className="form-input" value={m.unit} maxLength={MAX_UNIT} onChange={(e) => update(m.key, { unit: e.target.value })} placeholder="ör. kg, adet" />
                            </div>
                            <div className="form-field"><label className="lbl">Etiket</label>
                              <input className="form-input" value={m.label} maxLength={MAX_LABEL} onChange={(e) => update(m.key, { label: e.target.value })} placeholder="(başlık metni)" />
                            </div>
                          </div>
                          <div className="dash-me-r fmt">{layoutControl(m)}{alignControl(m)}{fontSizeControl(m)}{weightControl(m)}{colorControl(m)}</div>
                          {labelStyleBlock(m)}
                        </>
                      ) : m.type === "formula" ? (
                        <>
                          <FormulaEditor
                            candidates={formulaCandidates(m)}
                            value={m.formula}
                            onChange={(f) => update(m.key, { formula: f })}
                            hint="İşlemler soldan sağa uygulanır. Operandlar bu karttaki diğer öğelerdir — farklı formlardan/tesislerden metrikler birbiriyle hesaplanabilir."
                          />
                          <div className="dash-me-r data3">
                            <div className="form-field"><label className="lbl">Birim</label>
                              <input className="form-input" value={m.unit} maxLength={MAX_UNIT} onChange={(e) => update(m.key, { unit: e.target.value })} placeholder="ör. %, ton" />
                            </div>
                            <div className="form-field dash-me-span2"><label className="lbl">Etiket</label>
                              <input className="form-input" value={m.label} maxLength={MAX_LABEL} onChange={(e) => update(m.key, { label: e.target.value })} placeholder="(boş → Hesaplanan)" />
                            </div>
                          </div>
                          <div className="dash-me-r fmt">{layoutControl(m)}{alignControl(m)}{fontSizeControl(m)}{weightControl(m)}{colorControl(m)}</div>
                          {labelStyleBlock(m)}
                        </>
                      ) : m.type === "note" ? (
                        <div className="dash-me-r note">
                          <div className="form-field"><label className="lbl">Metin</label>
                            <textarea className="form-textarea" rows={2} maxLength={MAX_TEXT} value={m.text} onChange={(e) => update(m.key, { text: e.target.value })} placeholder="Bilgi notu…" />
                          </div>
                          {fontSizeControl(m)}{colorControl(m)}{alignControl(m)}
                        </div>
                      ) : m.type === "heading" ? (
                        <div className="dash-me-r text">
                          <div className="form-field"><label className="lbl">Başlık Metni</label>
                            <input className="form-input" value={m.text} maxLength={MAX_TEXT} onChange={(e) => update(m.key, { text: e.target.value })} placeholder="Başlık…" />
                          </div>
                          {fontSizeControl(m)}{weightControl(m)}{colorControl(m)}{alignControl(m)}
                        </div>
                      ) : (
                        // divider
                        <div className="dash-me-r data3"><div className="form-field"><label className="lbl">Çizgi rengi</label>
                          <div className="dash-color-row">
                            <label className="dash-color-toggle"><input type="checkbox" checked={m.customColor} onChange={(e) => update(m.key, { customColor: e.target.checked })} />Özel</label>
                            <input type="color" value={m.color} disabled={!m.customColor} onChange={(e) => update(m.key, { color: e.target.value })} aria-label="Çizgi rengi" />
                          </div>
                        </div></div>
                      )}
                    </div>
                  );
                })}
                <div className="dash-add-row">
                  {ADD_TYPES.map((t) => (
                    <button key={t.type} type="button" className="btn-md secondary" onClick={() => addItem(t.type)} disabled={items.length >= 12}>
                      <Icon name={t.icon} size={13} />{ITEM_TYPE_LABELS[t.type]}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="dash-me-r chart">
                  <div className="form-field"><label className="lbl">Form <span className="req">*</span></label>
                    <select className="form-select" value={chFormId} onChange={(e) => onChFormChange(e.target.value)}>
                      {catalog.map((c) => <option key={c.formId} value={c.formId}>{c.formName}{c.tesisName ? ` · ${c.tesisName}` : ""}</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label className="lbl">Alan (sayısal) <span className="req">*</span></label>
                    <select className="form-select" value={chFieldId} onChange={(e) => setChFieldId(e.target.value)} disabled={fieldsFor(chFormId).length === 0}>
                      {fieldsFor(chFormId).length === 0 ? <option value="">— sayısal alan yok —</option> : fieldsFor(chFormId).map((f) => <option key={f.id} value={f.id}>{f.label}{f.unit ? ` (${f.unit})` : ""}</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label className="lbl">Hesap <span className="req">*</span></label>
                    <select className="form-select" value={chCalc} onChange={(e) => setChCalc(e.target.value as DashCalc)}>
                      {CALC_OPTIONS.map((c) => <option key={c} value={c}>{CALC_LABELS[c]}</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label className="lbl">Grafik Türü <span className="req">*</span></label>
                    <select className="form-select" value={chartType} onChange={(e) => setChartType(e.target.value as DashChartType)}>
                      {(["bar", "line", "area", "donut"] as DashChartType[]).map((t) => <option key={t} value={t}>{CHART_LABELS[t]}</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label className="lbl">Gruplama <span className="req">*</span></label>
                    <select className="form-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value as DashGroupBy)}>
                      {(["day", "shift"] as DashGroupBy[]).map((g) => <option key={g} value={g}>{GROUPBY_LABELS[g]}</option>)}
                    </select>
                  </div>
                </div>
                <p className="help" style={{ marginTop: -4 }}>Seçilen hesap her gruba (gün/vardiya) uygulanır; grafik bu değerleri gösterir.</p>
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
